import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { array, fn } from '@ember/helper'
import { isEmpty } from '@ember/utils'
import { t } from 'ember-intl'
import type Store from '@ember-data/store'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { GradeSelect } from 'teacher-ui-client/components/base/select/grade-position'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { AllClassesOption } from 'teacher-ui-client/components/base/select/school-class/options'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import includes from 'teacher-ui-client/helpers/includes'
import DataTableOperations, { dtoComputed } from '@blakeelearning/data-tables/components/data-table-operations'
import doAction from 'teacher-ui-client/modifiers/do-action'
import { intersectionBy } from 'lodash'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import type Teacher from 'teacher-ui-client/models/teacher'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Signature {
  Args: {
    subscriptionType: SubscriptionType
    assignmentTeacherId: string
    selectedStudentIds: string[]
    updateSelectedStudentIds: (studentIds: string[]) => void
  }
}

interface StudentViewModel {
  id: string
  firstName: string
  lastName: string
  gradeName: string
  classNames: string
  gradePosition: number
  selected: boolean
  schoolClasses: SchoolClass[]
}

type DTOComponentContext = any

/**
 * Returns true if all items in the current filter scope are selected.
 */
function selectAllHelper(filterScopedItems: StudentViewModel[], selectedStudentIds: string[]) {
  return filterScopedItems.every((item) => selectedStudentIds.includes(item.id))
}

export class SelectStudentsTable extends Component<Signature> {
  @service store!: Store

  @service gradeSets!: GradeSetsService

  @tracked searchFilter = ''

  @tracked gradeFilter: number = AllGradesOption.id

  @tracked classFilter: string = AllClassesOption.id

  /**
   * Return school classes for the given teacher that are relevant - they match the subtype and have students
   */
  get schoolClassesOfAssignmentTeacher() {
    return (
      this.store
        .peekRecord<Teacher>('teacher', this.args.assignmentTeacherId)
        ?.schoolClassesForSubscriptionType(this.args.subscriptionType)
        .filter((schoolClass) => schoolClass.hasStudents) ?? []
    )
  }

  /**
   * Return uniq students that belong to the given teacher
   */
  get studentsOfAssignmentTeacher() {
    return [...new Set(this.schoolClassesOfAssignmentTeacher.flatMap((schoolClass) => schoolClass.students))]
  }

  /**
   * Return uniq, sorted grades for the students
   */
  get studentGrades() {
    return [...new Set(this.studentsOfAssignmentTeacher.map((student) => student.gradePosition))].sort((a, b) => a - b)
  }

  get studentTableRows(): StudentViewModel[] {
    const selectedStudentIds = this.args.selectedStudentIds
    return this.studentsOfAssignmentTeacher.filter(hasId).map((student) => {
      const { gradePosition } = student
      const gradeName = this.gradeSets.findByPosition(gradePosition)?.name ?? '-'
      const schoolClasses = intersectionBy(student.schoolClasses, this.schoolClassesOfAssignmentTeacher, 'id')
      const classNames = schoolClasses.map((schoolClass) => schoolClass.name).join(', ')
      return {
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        gradeName,
        classNames,
        gradePosition,
        selected: selectedStudentIds.includes(student.id),
        schoolClasses,
      }
    })
  }

  get dataOperations() {
    return {
      doFilter: dtoComputed(
        'data.[]',
        'selectedStudentIds.[]',
        'searchFilter',
        'gradeFilter',
        'classFilter',
        function (this: DTOComponentContext, data: StudentViewModel[]) {
          return data.filter((item) => {
            // do not filter out selected students
            if (this.selectedStudentIds.includes(item.id)) return true

            let shouldInclude = true

            // filter by search filter, if present
            const filterText = this.searchFilter.toLowerCase()
            if (!isEmpty(filterText)) {
              const { firstName, lastName, gradeName, classNames } = item
              shouldInclude &&= `${firstName}|${lastName}|${gradeName}|${classNames}`.toLowerCase().includes(filterText)
            }
            // filter by grade filter, if present
            const doGradeFilter = this.gradeFilter !== AllGradesOption.id
            if (shouldInclude && doGradeFilter) {
              shouldInclude &&= item.gradePosition === this.gradeFilter
            }
            // filter by selected class, if present
            const doClassFilter = this.classFilter !== AllClassesOption.id
            if (shouldInclude && doClassFilter) {
              shouldInclude &&= item.schoolClasses.some((schoolClass) => schoolClass.id === this.classFilter)
            }
            return shouldInclude
          })
        },
      ),
    }
  }

  operationsStack(this: DTOComponentContext) {
    return [...this.defaultOperationsStack, 'doFilter']
  }

  updateSearchFilter = (searchInput: string) => {
    this.searchFilter = searchInput
  }

  updateGradeFilter = (gradePosition: number) => {
    this.gradeFilter = gradePosition
  }

  updateClassFilter = (schoolClassId: string) => {
    this.classFilter = schoolClassId
  }

  toggleStudent = (studentId: string) => {
    if (this.args.selectedStudentIds.includes(studentId)) {
      this.args.updateSelectedStudentIds?.(this.args.selectedStudentIds.filter((id) => id !== studentId))
    } else {
      this.args.updateSelectedStudentIds?.([...this.args.selectedStudentIds, studentId])
    }
  }

  toggleStudents = (selectNone: boolean, items: StudentViewModel[]) => {
    if (selectNone) {
      this.args.updateSelectedStudentIds?.([])
    } else {
      this.args.updateSelectedStudentIds?.(items.map((item) => item.id))
    }
  }

  /**
   * We want to reset the filters when the assignment teacher changes
   */
  resetFilters = () => {
    this.searchFilter = ''
    this.gradeFilter = AllGradesOption.id
    this.classFilter = AllClassesOption.id
  }

  /**
   * NOTE: Current implementation of DTO mutates these objects, applying sortDirection etc to each
   */
  get columns() {
    return [{ key: 'firstName' }, { key: 'lastName' }, { key: 'gradeName' }, { key: 'classNames' }]
  }

  get productHoverColor() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return 'hover:bg-oceany-blue-100'
      case SubscriptionType.Writing:
        return 'hover:bg-wl-blue-100'
      case SubscriptionType.Maths:
        return 'hover:bg-ms-green-100'
      default:
        return ''
    }
  }

  <template>
    <div
      class="flex flex-wrap items-center justify-between gap-3 p-5"
      {{doAction this.resetFilters @assignmentTeacherId}}
    >
      <InputText
        type="search"
        role="searchbox"
        name="search-filter-student"
        class="grow"
        value={{this.searchFilter}}
        placeholder={{t "assignments.form.searchPlaceholder"}}
        aria-label={{t "assignments.form.searchPlaceholder"}}
        @themeKey={{themeKey @subscriptionType}}
        @icon="search"
        @iconEnd={{true}}
        {{on "input" (eventValue this.updateSearchFilter)}}
      />
      <GradeSelect
        class="grow"
        @additionalOptions={{array AllGradesOption}}
        @gradePositions={{this.studentGrades}}
        @gradePosition={{this.gradeFilter}}
        @updateGradePosition={{this.updateGradeFilter}}
        @themeKey={{themeKey @subscriptionType}}
      />
      <SchoolClassSelect
        class="grow"
        @additionalOptions={{array AllClassesOption}}
        @schoolClasses={{this.schoolClassesOfAssignmentTeacher}}
        @schoolClassId={{this.classFilter}}
        @updateSchoolClassId={{this.updateClassFilter}}
        @themeKey={{themeKey @subscriptionType}}
      />
    </div>
    <div class="flex justify-between">
      <div class="w-full overflow-x-auto">
        <DataTableOperations
          @data={{this.studentTableRows}}
          @dataOperations={{this.dataOperations}}
          @operationsStack={{this.operationsStack}}
          @columns={{this.columns}}
          @selectedStudentIds={{@selectedStudentIds}}
          @gradeFilter={{this.gradeFilter}}
          @searchFilter={{this.searchFilter}}
          @classFilter={{this.classFilter}}
          as |transformedData tableAction|
        >
          <table data-test-student-select-table class="divide-dusty-black-100 w-full divide-y">
            <thead>
              <tr class="divide-x divide-white">
                <th class="w-px text-center">
                  {{#let (selectAllHelper transformedData @selectedStudentIds) as |allSelected|}}
                    <label class="cursor-pointer px-4 py-2 select-none">
                      {{t "all"}}
                      <input
                        type="checkbox"
                        class="cursor-pointer"
                        checked={{allSelected}}
                        {{on "input" (fn this.toggleStudents allSelected transformedData)}}
                      />
                    </label>
                  {{/let}}
                </th>
                <th
                  class="cursor-pointer p-4 whitespace-nowrap"
                  data-heading={{t "assignments.firstName"}}
                  role="button"
                  {{on "click" (fn tableAction "sortByValue" "firstName")}}
                >
                  <span>
                    {{t "assignments.firstName"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" class="ml-4" role="presentation" />
                  </span>
                </th>
                <th
                  class="cursor-pointer p-4 whitespace-nowrap"
                  data-heading={{t "assignments.lastName"}}
                  role="button"
                  {{on "click" (fn tableAction "sortByValue" "lastName")}}
                >
                  <span>
                    {{t "assignments.lastName"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" class="ml-4" role="presentation" />
                  </span>
                </th>
                <th
                  class="cursor-pointer p-4 whitespace-nowrap"
                  data-heading={{t "grade.label"}}
                  role="button"
                  {{on "click" (fn tableAction "sortByValue" "gradeName")}}
                >
                  <span>
                    {{t "grade.label"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" class="ml-4" role="presentation" />
                  </span>
                </th>
                <th
                  class="cursor-pointer p-4 whitespace-nowrap"
                  data-heading={{t "assignments.classes"}}
                  role="button"
                  {{on "click" (fn tableAction "sortByValue" "classNames")}}
                >
                  <span>
                    {{t "assignments.classes"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" class="ml-4" role="presentation" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-dusty-black-100 divide-y bg-white">
              {{#each transformedData as |row|}}
                <tr data-test-assignment-row class={{this.productHoverColor}}>
                  <td class="text-center">
                    <label class="cursor-pointer p-4 select-none">
                      <input
                        type="checkbox"
                        class="cursor-pointer"
                        checked={{includes @selectedStudentIds row.id}}
                        {{on "input" (fn this.toggleStudent row.id)}}
                      />
                    </label>
                  </td>
                  <td class="p-4">
                    {{row.firstName}}
                  </td>
                  <td class="p-4">
                    {{row.lastName}}
                  </td>
                  <td class="p-4">
                    {{row.gradeName}}
                  </td>
                  <td class="p-4">
                    {{row.classNames}}
                  </td>
                </tr>
              {{else}}
                <tr data-test-assignment-row>
                  <td class="p-4" {{colspanMax}}>
                    {{t "assignments.form.noStudentsInTable"}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </DataTableOperations>
      </div>
    </div>
  </template>
}
